/** Contact invite payload — FR-CON-001 QR/share text (no phone number). */

export interface InvitePayloadV1 {
  v: 1;
  type: "veto.invite";
  displayName: string;
  messagingPublicKeyHex: string;
  hybridPublicKeyHex: string;
}

/** Signed identity bundle invite — preferred (binds Ed25519 ↔ hybrid). */
export interface InvitePayloadV2 {
  v: 2;
  type: "veto.invite";
  displayName: string;
  /** JSON from WasmIdentity.publicBundleJson() — verified via verifyIdentityBundle */
  identityBundleJson: string;
}

/**
 * Compact signed-bundle invite (v3) — same trust as v2, ~5x shorter (FR-CON-001).
 *
 * v2 embeds the identity bundle as JSON number-arrays, which costs ~4 chars/byte,
 * duplicates the ML-KEM key (`mlkem_pk` == `hybrid_public[32..]`), and then gains
 * another ~33% from the outer base64. v3 instead carries each key once as base64
 * and reconstructs the canonical v2 bundle JSON before verification, so the
 * `verifyIdentityBundle` transcript (SEC-ID-001) and suite check (SEC-CRY-006)
 * run unchanged. No protocol or crypto change — transport encoding only.
 */
export interface InvitePayloadV3 {
  v: 3;
  type: "veto.invite";
  /** Display name (same bounds as v1/v2). */
  n: string;
  /** Bundle protocol_version. */
  p: number;
  /** Bundle suite id — never defaulted, so downgrades stay loud (SEC-CRY-006). */
  s: string;
  /** messaging_ed25519_pk (32 bytes, base64). */
  e: string;
  /** messaging_x25519_pk (32 bytes, base64). */
  x: string;
  /** transport_secp256k1_pk, compressed (33 bytes, base64). */
  t: string;
  /** hybrid_public = X25519(32) || ML-KEM-768 EK (base64). */
  h: string;
  /** binding_sig (64 bytes, base64). */
  g: string;
}

export type InvitePayload = InvitePayloadV1 | InvitePayloadV2 | InvitePayloadV3;

/** SEC: bound pasted/deep-linked invite size (v2 bundles ~10KB; fail-closed above). */
export const MAX_INVITE_CHARS = 32768;
/** Display names are UI labels, not keys — cap to prevent storage/UI jank. */
export const MAX_DISPLAYNAME_CHARS = 64;

export function encodeInvite(payload: InvitePayload): string {
  const name = payload.v === 3 ? payload.n : payload.displayName;
  if (name.trim().length < 1 || name.length > MAX_DISPLAYNAME_CHARS) {
    throw new Error("Invalid display name");
  }
  return `veto-invite:${btoa(JSON.stringify(payload))}`;
}

/** Exact byte sizes for the fixed fields (variable: t=33 compressed secp, h=1216 hybrid). */
const V3_LEN = { e: 32, x: 32, t: 33, g: 64 } as const;
/** Wire bounds (DoS): hybrid is X25519(32) + ML-KEM EK; cap generously for suite agility. */
const V3_HYBRID_MIN = 64;
const V3_HYBRID_MAX = 4096;
const V3_SUITE_MAX = 128;

function bytesToB64(bytes: Uint8Array): string {
  let bin = "";
  const CHUNK = 512;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(bin);
}

function b64ToBytes(b64: string, expected: number | null, field: string): Uint8Array {
  let bin: string;
  try {
    bin = atob(b64);
  } catch {
    throw new Error(`Invalid or corrupt invite: bad ${field} encoding`);
  }
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  if (expected !== null && out.length !== expected) {
    throw new Error(`Invalid or corrupt invite: bad ${field} length`);
  }
  return out;
}

function numArrayToBytes(v: unknown, field: string): Uint8Array {
  if (!Array.isArray(v)) throw new Error(`Invalid or corrupt invite: bundle missing ${field}`);
  const out = new Uint8Array(v.length);
  for (let i = 0; i < v.length; i++) {
    const n = v[i];
    if (!Number.isInteger(n) || n < 0 || n > 255) {
      throw new Error(`Invalid or corrupt invite: bad ${field} byte`);
    }
    out[i] = n;
  }
  return out;
}

/**
 * Build a v3 compact invite from a v2 bundle JSON string.
 * Throws fail-closed on malformed bundles (FR-CON-001).
 */
export function encodeCompactInvite(displayName: string, identityBundleJson: string): string {
  if (displayName.trim().length < 1 || displayName.length > MAX_DISPLAYNAME_CHARS) {
    throw new Error("Invalid display name");
  }
  let bundle: {
    protocol_version: unknown;
    suite: unknown;
    messaging_ed25519_pk: unknown;
    messaging_x25519_pk: unknown;
    transport_secp256k1_pk: unknown;
    hybrid_public: unknown;
    binding_sig: unknown;
  };
  try {
    bundle = JSON.parse(identityBundleJson);
  } catch {
    throw new Error("Invalid or corrupt invite: bundle not valid JSON");
  }
  if (typeof bundle.protocol_version !== "number" || !Number.isInteger(bundle.protocol_version)) {
    throw new Error("Invalid or corrupt invite: bundle missing protocol_version");
  }
  if (typeof bundle.suite !== "string" || !bundle.suite || bundle.suite.length > V3_SUITE_MAX) {
    throw new Error("Invalid or corrupt invite: bundle missing suite");
  }
  const ed = numArrayToBytes(bundle.messaging_ed25519_pk, "messaging_ed25519_pk");
  const x = numArrayToBytes(bundle.messaging_x25519_pk, "messaging_x25519_pk");
  const t = numArrayToBytes(bundle.transport_secp256k1_pk, "transport_secp256k1_pk");
  const h = numArrayToBytes(bundle.hybrid_public, "hybrid_public");
  const g = numArrayToBytes(bundle.binding_sig, "binding_sig");
  if (ed.length !== V3_LEN.e || x.length !== V3_LEN.x || g.length !== V3_LEN.g) {
    throw new Error("Invalid or corrupt invite: bad bundle key length");
  }
  if (t.length !== V3_LEN.t || h.length < V3_HYBRID_MIN || h.length > V3_HYBRID_MAX) {
    throw new Error("Invalid or corrupt invite: bad bundle key length");
  }
  const payload: InvitePayloadV3 = {
    v: 3,
    type: "veto.invite",
    n: displayName,
    p: bundle.protocol_version,
    s: bundle.suite,
    e: bytesToB64(ed),
    x: bytesToB64(x),
    t: bytesToB64(t),
    h: bytesToB64(h),
    g: bytesToB64(g),
  };
  return `veto-invite:${btoa(JSON.stringify(payload))}`;
}

/**
 * Expand a verified-shape v3 payload back to canonical v2 bundle JSON.
 * `mlkem_pk` is redundant on the wire (`== hybrid_public[32..]`, enforced by
 * `verify_bundle`) and is reconstructed here so verification runs unchanged.
 */
export function compactInviteToBundleJson(inv: InvitePayloadV3): string {
  const ed = b64ToBytes(inv.e, V3_LEN.e, "ed25519 key");
  const x = b64ToBytes(inv.x, V3_LEN.x, "x25519 key");
  const t = b64ToBytes(inv.t, V3_LEN.t, "transport key");
  const h = b64ToBytes(inv.h, null, "hybrid key");
  const g = b64ToBytes(inv.g, V3_LEN.g, "binding signature");
  if (h.length < V3_HYBRID_MIN || h.length > V3_HYBRID_MAX) {
    throw new Error("Invalid or corrupt invite: bad hybrid key length");
  }
  if (!Number.isInteger(inv.p)) throw new Error("Invalid or corrupt invite: bad protocol version");
  if (typeof inv.s !== "string" || !inv.s || inv.s.length > V3_SUITE_MAX) {
    throw new Error("Invalid or corrupt invite: bad suite");
  }
  return JSON.stringify({
    protocol_version: inv.p,
    suite: inv.s,
    messaging_ed25519_pk: [...ed],
    messaging_x25519_pk: [...x],
    transport_secp256k1_pk: [...t],
    mlkem_pk: [...h.slice(32)],
    hybrid_public: [...h],
    binding_sig: [...g],
  });
}

/**
 * Decode any known invite version. v3 compact invites normalize to the
 * equivalent v2 shape, so callers only ever see v1 | v2 at runtime.
 */
export function decodeInvite(raw: string): InvitePayloadV1 | InvitePayloadV2 {
  const trimmed = raw.trim();
  if (!trimmed) {
    throw new Error("Invalid or corrupt invite: empty");
  }
  if (trimmed.length > MAX_INVITE_CHARS) {
    throw new Error("Invalid or corrupt invite: too large");
  }
  const body = trimmed.startsWith("veto-invite:")
    ? trimmed.slice("veto-invite:".length)
    : trimmed;
  let json: string;
  try {
    json = atob(body);
  } catch {
    throw new Error("Invalid or corrupt invite: not valid base64");
  }
  let parsed: InvitePayload;
  try {
    parsed = JSON.parse(json) as InvitePayload;
  } catch {
    throw new Error("Invalid or corrupt invite: not valid JSON");
  }
  if (parsed?.type !== "veto.invite") {
    throw new Error("Invalid or corrupt invite: unrecognized payload");
  }
  if ((parsed as { v?: unknown }).v === 3) {
    const v3 = parsed as InvitePayloadV3;
    if (typeof v3.n !== "string" || !v3.n.trim()) {
      throw new Error("Invalid or corrupt invite: missing display name");
    }
    if (v3.n.length > MAX_DISPLAYNAME_CHARS) {
      throw new Error("Invalid or corrupt invite: display name too long");
    }
    // Normalize to v2 shape: reconstruct + shape-validate now (the signature
    // itself is verified downstream via verifyIdentityBundle). Existing v2
    // consumers (FR-CON-001 add-contact, scripts) work unchanged.
    const identityBundleJson = compactInviteToBundleJson(v3);
    if (identityBundleJson.length > MAX_INVITE_CHARS) {
      throw new Error("Invalid or corrupt invite: bundle too large");
    }
    return { v: 2, type: "veto.invite", displayName: v3.n, identityBundleJson };
  }
  if (typeof (parsed as InvitePayloadV1).displayName !== "string" || !(parsed as InvitePayloadV1).displayName.trim()) {
    throw new Error("Invalid or corrupt invite: missing display name");
  }
  if ((parsed as InvitePayloadV1).displayName.length > MAX_DISPLAYNAME_CHARS) {
    throw new Error("Invalid or corrupt invite: display name too long");
  }
  if (parsed.v === 2) {
    if (!parsed.identityBundleJson) throw new Error("Invalid or corrupt invite: missing identity bundle");
    if (parsed.identityBundleJson.length > MAX_INVITE_CHARS) {
      throw new Error("Invalid or corrupt invite: bundle too large");
    }
    return parsed;
  }
  if (parsed.v === 1) {
    if (!parsed.messagingPublicKeyHex || !parsed.hybridPublicKeyHex) {
      throw new Error("Invalid or corrupt invite: missing public keys");
    }
    if (parsed.messagingPublicKeyHex.length > 256 || parsed.hybridPublicKeyHex.length > 8192) {
      throw new Error("Invalid or corrupt invite: keys too large");
    }
    return parsed;
  }
  throw new Error("Invalid or corrupt invite: unsupported version");
}

/** Normalize digits for safety-number comparison (spaces/dashes ignored). */
export function normalizeSafetyDigits(input: string): string {
  return input.replace(/\D/g, "");
}

// ---------------------------------------------------------------------------
// v4 invite LINKS (protocol v2) — FR-CON-001 "as easy as sharing a link"
// ---------------------------------------------------------------------------
//
//   https://<origin>/app#i=<base64url( 0x04 ‖ nameLen:u8 ‖ name:UTF-8 ‖ compactBundle )>
//
// * The payload sits in the URL FRAGMENT: browsers never send it to the server,
//   so opening someone's invite reveals nothing to the web host.
// * UTF-8 names (v1–v3 used btoa() and crashed on "Łukasz", "Дмитрий", emoji).
// * The signed compact bundle (1378 bytes) is verified by the crypto core before
//   any key is trusted; the name is only a suggestion the user can edit.
// * ~1.9k chars → QR version 32 (ECC L): scannable, unlike v3 (2.6k).

export const INVITE_LINK_VERSION = 4;
/** Compact signed identity bundle (crypto core `COMPACT_BUNDLE_LEN`). */
export const COMPACT_BUNDLE_BYTES = 1378;
const MAX_NAME_BYTES = 192;

export interface InviteLinkPayload {
  /** Suggested display name (unsigned, editable). */
  name: string;
  /** Signed compact bundle — verify before use. */
  bundle: Uint8Array;
}

function toBase64Url(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    bin += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + 0x8000)));
  }
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64Url(s: string): Uint8Array {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((s.length + 3) % 4);
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Strip control characters and cap a display name to 64 characters. */
export function sanitizeDisplayName(name: string): string {
  return Array.from(name.replace(/[\u0000-\u001f\u007f\u202a-\u202e\u2066-\u2069]/g, "").trim())
    .slice(0, MAX_DISPLAYNAME_CHARS)
    .join("");
}

/** Encode the fragment token (`#i=<token>`). */
export function encodeInviteToken(name: string, compactBundle: Uint8Array): string {
  if (compactBundle.length !== COMPACT_BUNDLE_BYTES) throw new Error("Invalid identity bundle");
  const chars = Array.from(sanitizeDisplayName(name));
  let nameBytes = new TextEncoder().encode(chars.join(""));
  while (nameBytes.length > MAX_NAME_BYTES && chars.length > 0) {
    chars.pop();
    nameBytes = new TextEncoder().encode(chars.join(""));
  }
  const out = new Uint8Array(2 + nameBytes.length + compactBundle.length);
  out[0] = INVITE_LINK_VERSION;
  out[1] = nameBytes.length;
  out.set(nameBytes, 2);
  out.set(compactBundle, 2 + nameBytes.length);
  return toBase64Url(out);
}

export function encodeInviteLink(opts: { origin: string; name: string; compactBundle: Uint8Array }): string {
  const origin = opts.origin.replace(/\/+$/, "");
  return `${origin}/app#i=${encodeInviteToken(opts.name, opts.compactBundle)}`;
}

/** Decode a fragment token; throws on malformed input (signature checked by caller). */
export function decodeInviteToken(token: string): InviteLinkPayload {
  if (!/^[A-Za-z0-9_-]{100,4096}$/.test(token)) throw new Error("Invalid or corrupt invite link");
  let bytes: Uint8Array;
  try {
    bytes = fromBase64Url(token);
  } catch {
    throw new Error("Invite link looks truncated or damaged — ask them to send it again");
  }
  if (bytes[0] !== INVITE_LINK_VERSION) throw new Error("Unsupported invite link version — update Veto");
  const nameLen = bytes[1] ?? 0;
  if (bytes.length !== 2 + nameLen + COMPACT_BUNDLE_BYTES) {
    throw new Error("Invite link looks truncated or damaged — ask them to send it again");
  }
  let name = "";
  try {
    name = new TextDecoder("utf-8", { fatal: true }).decode(bytes.subarray(2, 2 + nameLen));
  } catch {
    name = "";
  }
  return { name: sanitizeDisplayName(name), bundle: bytes.slice(2 + nameLen) };
}

export type ExtractedInvite =
  | { kind: "link"; token: string }
  | { kind: "legacy"; text: string };

/**
 * Find an invite anywhere in pasted / shared / scanned text: a v4 link (or bare
 * `i=` token) or a legacy `veto-invite:` string. Lets people paste the WHOLE
 * message they received, not just the invite part.
 */
export function extractInvite(text: string): ExtractedInvite | null {
  if (!text || text.length > MAX_INVITE_CHARS * 2) return null;
  const link = /(?:^|[#?&\s/])i=([A-Za-z0-9_-]{100,4096})/.exec(text);
  if (link?.[1]) return { kind: "link", token: link[1] };
  const legacy = /veto-invite:[A-Za-z0-9+/=]{40,}/.exec(text);
  if (legacy) return { kind: "legacy", text: legacy[0] };
  const bare = text.trim();
  if (/^[A-Za-z0-9_-]{1800,2400}$/.test(bare)) return { kind: "link", token: bare };
  return null;
}
